import geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import ImageryPreviewImage from 'app/components/OrderImageryModal/ImageryPreviewImage';
import {ApiFeature, ApiOrderableScene} from 'app/modules/Remote/Feature';
import * as featureUtils from 'app/utils/featureUtils';
import {getScenePreviewKind, getThumbnailOverrideScene} from 'app/utils/imageryUtils';

/** Everything the preview needs to draw one scene over its property. */
export interface ScenePreviewSource {
  feature: I.ImmutableOf<ApiFeature>;
  scene: I.ImmutableOf<ApiOrderableScene>;
  /** Every scene of the feature: a multispectral scene borrows its truecolor sibling's thumbnail. */
  orderableScenes?: I.List<I.ImmutableOf<ApiOrderableScene>>;
  subsetFeature?: geojson.Feature<geojson.Polygon | geojson.MultiPolygon> | null;
}

export const ScenePreview: React.FC<{
  source: ScenePreviewSource;
  size: number;
  blockedSources?: string[];
  isFeatureHidden?: boolean;
}> = ({source, size, blockedSources, isFeatureHidden}) => {
  const {feature, scene, orderableScenes, subsetFeature} = source;

  // Memoised so the downstream projection math only reruns when the scene itself changes.
  const preview = React.useMemo(() => {
    const overrideScene = getThumbnailOverrideScene(scene, orderableScenes);
    const sceneForPreview = overrideScene ?? scene;
    const {hasPreviewImage} = getScenePreviewKind(scene);
    return {
      url: hasPreviewImage ? featureUtils.imageAndSourceDetailsFromScene(scene).url : undefined,
      thumbnailOverrideUrl: overrideScene
        ? featureUtils.imageAndSourceDetailsFromScene(overrideScene).url
        : undefined,
      bounds: sceneForPreview.get('thumbnailBbox').toJS(),
      sceneGeometry: sceneForPreview.get('sceneGeometry').toJS(),
      orderedGeometry: scene.get('orderedGeometry')?.toJS(),
    };
  }, [scene, orderableScenes]);

  return (
    <ImageryPreviewImage
      feature={feature}
      maxWidth={size}
      maxHeight={size}
      url={preview.url}
      bounds={preview.bounds}
      sourceId={scene.get('sourceId')}
      sceneGeometry={preview.sceneGeometry}
      blockedSources={blockedSources}
      isFeatureHidden={isFeatureHidden}
      subsetFeature={subsetFeature ?? null}
      orderedGeometry={preview.orderedGeometry}
      thumbnailOverrideUrl={preview.thumbnailOverrideUrl}
    />
  );
};
